import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, ArrowLeft, Trophy, Star } from 'lucide-react';
import { iceLogo } from '../mock';

const IAN_PHOTO = 'https://customer-assets-gfyr7b9c.emergentagent.net/job_navy-red-hub/artifacts/wctlpnn3_ed9addf9-8470-44d5-9e7e-aca224fb1a62.jpeg';

const TRIBUTE = `Welcome to ICE Brookline Jamaica Plain Pop-Warner Football & Cheerleading, renamed in honor of Ian Cotterell. Ian was one of the founders of the Brookline Jamaica Plain Patriots. He was tremendously loved and respected by everyone who came through the program because of his passion, dedication, and commitment to helping the young men and women in the program develop into respectful and responsible adults. Although Ian has passed on, we strive to continue his mission of building a strong community and uplifting children through the sport he loved most. Welcome to our page! We hope to see you all on the field, cheering on our young athletes and supporting them as they grow both on and off the field. Go Patriots!`;

export default function Tribute() {
  return (
    <div className="min-h-screen bg-white font-body" data-testid="tribute-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#0a1f44] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 20% 20%, #c8102e 0, transparent 45%), radial-gradient(circle at 80% 80%, #c8102e 0, transparent 45%)',
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6 text-center text-white">
          <Link to="/" data-testid="tribute-back-btn" className="inline-flex items-center gap-2 font-heading uppercase tracking-widest text-[10px] text-white/60 hover:text-white mb-6">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Home
          </Link>
          <div className="inline-flex items-center gap-2 font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">
            <Heart className="w-4 h-4 fill-current" /> In Loving Memory
          </div>
          <h1 className="font-display text-5xl md:text-7xl leading-tight mb-4">IAN COTTERELL</h1>
          <div className="font-heading uppercase tracking-widest text-white/70 text-sm">Founder · Brookline Jamaica Plain Patriots</div>
        </div>
      </section>

      {/* Portrait + story */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-start">
          <div className="relative aspect-[3/4] overflow-hidden bg-[#0a1f44] border-t-4 border-[#c8102e] shadow-2xl" data-testid="tribute-portrait-wrap">
            <img
              src={IAN_PHOTO}
              alt="Ian Cotterell — founder of the Brookline Jamaica Plain Patriots"
              className="w-full h-full object-cover"
              style={{ objectPosition: '72% 24%', transform: 'scale(1.65)', transformOrigin: '72% 24%' }}
              data-testid="tribute-portrait"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f44]/40 via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-4 left-4 bg-[#0a1f44] text-white px-4 py-2 shadow-lg">
              <div className="font-heading uppercase tracking-[0.3em] text-[10px] text-[#c8102e]">Champion at Heart</div>
              <div className="font-display text-lg">Ian "ICE" Cotterell</div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs">A Founder's Legacy</div>
            <p className="text-[#0a1f44]/85 text-lg leading-relaxed">{TRIBUTE}</p>

            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-[#0a1f44]/10">
              <Stat icon={<Trophy className="w-5 h-5" />} label="Program Founder" value="20+ Yrs" />
              <Stat icon={<Star className="w-5 h-5" />} label="Youth Impacted" value="500+" />
              <Stat icon={<Heart className="w-5 h-5" />} label="Mission" value="Forever" />
            </div>
          </div>
        </div>
      </section>

      {/* Pull quote */}
      <section className="py-16 bg-[#f5f6f8]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-[#c8102e] text-5xl font-display leading-none mb-4">"</div>
          <blockquote className="font-display text-2xl md:text-3xl text-[#0a1f44] leading-snug" data-testid="tribute-quote">
            Passion, dedication, and commitment to helping the young men and women in the program develop into respectful and responsible adults.
          </blockquote>
          <div className="mt-6 font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[11px]">— The Ian Cotterell Way</div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0a1f44] text-white text-center">
        <div className="max-w-2xl mx-auto px-6">
          <img src={iceLogo} alt="ICE" className="w-20 h-auto mx-auto mb-4" />
          <h2 className="font-display text-3xl md:text-4xl mb-3">CARRY THE LEGACY</h2>
          <p className="text-white/70 mb-6">Join a program built on Ian's values — passion, dedication, and community. Register your athlete today.</p>
          <Link
            to="/register"
            data-testid="tribute-register-cta"
            className="inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-xs px-8 py-4 shadow-lg shadow-[#c8102e]/40 transition-all"
          >
            Register Now
          </Link>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="text-center">
      <div className="text-[#c8102e] flex justify-center mb-1">{icon}</div>
      <div className="font-display text-xl text-[#0a1f44] leading-none">{value}</div>
      <div className="font-heading uppercase tracking-widest text-[9px] text-[#0a1f44]/60 mt-1">{label}</div>
    </div>
  );
}
